import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router, ActivatedRoute } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit {
  public loginInProgress = false;
  public sessionExpired = false;
  public errorMessage = '';

  constructor(
    private http: HttpClient,
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService
  ) {
    // Check for auth code on page load
    this.handleAuthCallback();
    
    // Check query params for expired session
    this.route.queryParams.subscribe(params => {
      if (params['expired'] === 'true') {
        this.sessionExpired = true;
        this.errorMessage = 'Your session has expired. Please log in again.';
        console.log('Session expired param detected');
      }
    });
  }

  ngOnInit() {
    // Clean up any invalid tokens that might be in localStorage
    if (this.authService.isTokenExpired(0)) {
      console.log('Invalid token detected on login page, clearing auth data');
      this.authService.clearAuthData();
    }
    
    // If no auth code in URL and not logged in, redirect to Microsoft login
    if (!this.isCodeInUrl() && !this.authService.isLoggedIn()) {
      console.log('No auth code or token found, redirecting to Microsoft login');
      this.initiateLogin();
    } else if (this.authService.isLoggedIn()) {
      // If already logged in, redirect to dashboard
      console.log('Already authenticated, redirecting to dashboard');
      this.router.navigate(['/dispatch_dash']);
    }
  }

  // Check if auth code is in URL
  private isCodeInUrl(): boolean {
    const urlParams = new URLSearchParams(window.location.search);
    return !!urlParams.get('code');
  }

  // Initiate login with Microsoft
  private async initiateLogin() {
    this.loginInProgress = true;
    
    // Generate PKCE code verifier and challenge
    const codeVerifier = this.generateRandomString(64);
    const codeChallenge = await this.generateCodeChallenge(codeVerifier);
    
    // Store code verifier for later
    localStorage.setItem('pkce_code_verifier', codeVerifier);
    
    // Configure auth request
    const tenantId = environment.azureTenantId;
    const clientId = environment.backendClientId;
    const redirectUri = encodeURIComponent(window.location.origin + '/login');
    
    // Build login URL with PKCE
    const loginUrl = 
      `https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/authorize?` +
      `client_id=${clientId}` +
      `&response_type=code` +
      `&redirect_uri=${redirectUri}` +
      `&response_mode=query` +
      `&scope=api://${clientId}/user_impersonation openid profile email` +
      `&state=${this.generateRandomString(16)}` +
      `&code_challenge=${codeChallenge}` +
      `&code_challenge_method=S256` +
      `&prompt=select_account`;
    
    // Redirect to Microsoft login
    window.location.href = loginUrl;
  }

  // Handle the auth callback
  private handleAuthCallback() {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    
    if (code) {
      console.log('Auth code received, exchanging for token...');
      this.loginInProgress = true;
      this.exchangeCodeForToken(code);
    }
  }
  
  // Exchange code for token
  private exchangeCodeForToken(code: string) {
    console.log('Exchanging code for token...');
    
    const tenantId = environment.azureTenantId;
    const clientId = environment.backendClientId;
    const redirectUri = window.location.origin + '/login';
    const codeVerifier = localStorage.getItem('pkce_code_verifier') || '';
    
    console.log('Using redirect URI:', redirectUri);
    
    const body = new URLSearchParams();
    body.set('grant_type', 'authorization_code');
    body.set('client_id', clientId);
    body.set('scope', `api://${clientId}/user_impersonation openid profile email`);
    body.set('code', code);
    body.set('redirect_uri', redirectUri);
    body.set('code_verifier', codeVerifier);
    
    // Use fetch API for more direct control
    fetch(`https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: body.toString()
    })
    .then(response => {
      if (!response.ok) {
        console.error('Token exchange failed:', response.status, response.statusText);
        this.errorMessage = 'Failed to authenticate. Please try again.';
        throw new Error('Token exchange failed');
      }
      return response.json();
    })
    .then(data => {
      console.log('Token received:', data.access_token ? 'Token exists' : 'No token');
      
      if (data.access_token) {
        localStorage.removeItem('pkce_code_verifier');
        localStorage.setItem('access_token', data.access_token);
        if (data.refresh_token) {
          localStorage.setItem('refresh_token', data.refresh_token);
        }
        this.fetchUserProfile(data.access_token, data.refresh_token);
        // Clear any error messages
        this.errorMessage = '';
        this.sessionExpired = false;
      } else {
        console.error('No access token in response:', data);
        this.loginInProgress = false;
        this.errorMessage = 'Authentication failed. No access token received.';
      }
    })
    .catch(error => {
      console.error('Token exchange error:', error);
      this.loginInProgress = false;
      localStorage.removeItem('pkce_code_verifier');
      if (!this.errorMessage) {
        this.errorMessage = 'An error occurred during authentication.';
      }
    });
  }

  
  
  // Fetch user profile from backend
  private fetchUserProfile(token: string, refreshToken: string | null) {
    console.log('Fetching user profile with token');
    
    // Token should already be stored by now
    
    this.http.get(environment.apiUrl + '/api/me', {
      headers: { 'Authorization': `Bearer ${token}` }
    }).subscribe({
      next: (profile: any) => {
        console.log('Profile received, setting auth data');
        
        // Set auth data in the service
        this.authService.setAuthData(token, refreshToken, profile);
        
        // Reset login state
        this.loginInProgress = false;
        
        // Clear the URL after successful authentication
        window.history.replaceState({}, document.title, '/dashboard');
        
        // Navigate to dashboard
        this.router.navigate(['/dashboard']);
      },
      error: (error) => {
        console.error('Profile fetch error:', error);
        
        // Even if profile fetch fails, we have a token, so we can still authenticate
        // Create a minimal profile
        const minimalProfile = { name: 'User' };
        
        // Set auth data with the token and minimal profile
        this.authService.setAuthData(token, refreshToken, minimalProfile);
        
        this.loginInProgress = false;
        
        // Navigate to dashboard
        this.router.navigate(['/dashboard']);
      }
    });
  }
  
  // Generate random string for state and code verifier
  private generateRandomString(length: number): string {
    const array = new Uint8Array(length);
    window.crypto.getRandomValues(array);
    return this.base64UrlEncode(array);
  }
  
  // Generate code challenge from verifier
  private async generateCodeChallenge(verifier: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(verifier);
    const digest = await window.crypto.subtle.digest('SHA-256', data);
    return this.base64UrlEncode(new Uint8Array(digest));
  }
  
  // Base64Url encoding
  private base64UrlEncode(buffer: Uint8Array): string {
    return btoa(String.fromCharCode(...buffer))
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  }

  // Manual login action that can be triggered by a button
  login() {
    this.initiateLogin();
  }

  logout() {
    this.authService.logout();
  }
}